import { Box, Typography } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";

import { ds } from "../../theme/ds";
import { chartPalette } from "../../theme/tokens";
import type { WidgetProps } from "../types";

import { Card, SourceLine } from "./card";
import { formatCurrency0, formatGeneratedAt, formatInt } from "./format";

// Parts of a whole as donuts, one or more side by side under one title (exclusivity
// case study, Elijah 2026-10-27: the growth by marketplace, by assortment band and the
// ad-driven share of sales). Each donut has its own heading, an optional figure in the
// hole, its share on each arc and a legend underneath; slices take chartPalette in
// order. Only for values that add up to a whole: growth rates or anything negative
// belong in a bar chart, and a donut of gains says what it leaves out in its note.
export function DonutChartWidget({ widget }: WidgetProps<"donut_chart">) {
    const { title, caption, format = "number", source, hideSource } = widget.props;
    const { donuts, generatedAt } = widget.data;
    const print = (v: number) => (format === "currency" ? formatCurrency0(v) : formatInt(v));

    return (
        <Card sx={{ display: "flex", flexDirection: "column" }}>
            <Typography component="div" sx={{ fontSize: 16, fontWeight: 700, color: ds.navy, lineHeight: 1.3 }}>
                {title}
            </Typography>
            {caption && (
                <Typography component="div" sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: "4px", maxWidth: 760 }}>
                    {caption}
                </Typography>
            )}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: `repeat(${donuts.length}, minmax(0, 1fr))` },
                    gap: 3,
                    mt: 2,
                }}
            >
                {donuts.map((d) => (
                    <Donut key={d.title} donut={d} print={print} />
                ))}
            </Box>
            {/* Opt-out for a pitch page; the source stays required in the data. */}
            {!hideSource && (
                <SourceLine sx={{ pt: "8px" }}>
                    {source} · {formatGeneratedAt(generatedAt)}
                </SourceLine>
            )}
        </Card>
    );
}

type DonutData = WidgetProps<"donut_chart">["widget"]["data"]["donuts"][number];

function Donut({ donut, print }: { donut: DonutData; print: (v: number) => string }) {
    const total = donut.slices.reduce((s, x) => s + x.value, 0);
    const share = (v: number) => `${Math.round((v / total) * 100)}%`;
    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 0 }}>
            <Typography sx={{ fontSize: ds.text.sm, fontWeight: 700, color: ds.navy, mb: 1, textAlign: "center" }}>
                {donut.title}
            </Typography>
            <Box sx={{ position: "relative", width: 210, height: 210 }}>
                <PieChart
                    width={210}
                    height={210}
                    margin={{ top: 5, bottom: 5, left: 5, right: 5 }}
                    series={[
                        {
                            data: donut.slices.map((s, i) => ({ id: i, value: s.value, label: s.label, color: chartPalette[i] })),
                            innerRadius: 62,
                            outerRadius: 100,
                            paddingAngle: 1.5,
                            cornerRadius: 3,
                            arcLabel: (item) => share(item.value),
                            arcLabelMinAngle: 22,
                            valueFormatter: (item) => `${print(item.value)} (${share(item.value)})`,
                        },
                    ]}
                    slotProps={{ legend: { hidden: true } }}
                    sx={{ "& .MuiPieArcLabel-root": { fill: ds.white, fontSize: 12, fontWeight: 700 } }}
                />
                {donut.center && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                        }}
                    >
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: ds.navy, lineHeight: 1.1 }}>
                            {donut.center.value}
                        </Typography>
                        {donut.center.label && (
                            <Typography sx={{ fontSize: ds.text.xs, color: ds.gray500 }}>{donut.center.label}</Typography>
                        )}
                    </Box>
                )}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mt: 1.5, width: "100%", maxWidth: 260 }}>
                {donut.slices.map((s, i) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, flex: "none", borderRadius: "2px", bgcolor: chartPalette[i] }} />
                        <Typography sx={{ fontSize: ds.text.xs, color: ds.navy, flex: 1, minWidth: 0 }}>{s.label}</Typography>
                        <Typography sx={{ fontSize: ds.text.xs, color: ds.gray500, whiteSpace: "nowrap" }}>
                            {print(s.value)} · {share(s.value)}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {donut.note && (
                <Typography sx={{ fontSize: ds.text.xs, color: ds.gray500, mt: 1, maxWidth: 260 }}>{donut.note}</Typography>
            )}
        </Box>
    );
}
